import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Loader, AlertCircle, ShieldCheck, Pencil, Plus, Search } from 'lucide-react';
import { useOnClickOutside } from '../../hooks/useOnClickOutside';
import { useDebounce } from '../../hooks/useDebounce';
import { eventTeamApiService } from '../../services/eventTeamApi';
import type {
  EventCheckMatchMode,
  EventCheckStatus,
  EventCommandCheck,
  EventItem,
} from '../../types/eventTeam';
import { EVENT_CHECK_MATCH_MODES, EVENT_CHECK_STATUSES } from '../../types/eventTeam';

export interface CommandCheckModalProps {
  isOpen: boolean;
  mode: 'create' | 'edit';
  check?: EventCommandCheck | null;
  presetEventId?: string;
  onClose: () => void;
  onSuccess: (check: EventCommandCheck) => void;
}

function msToLocalInput(ms: number | null | undefined): string {
  if (ms == null) return '';
  const d = new Date(ms);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function localInputToMs(value: string): number | null {
  if (!value) return null;
  const ms = new Date(value).getTime();
  return Number.isFinite(ms) ? ms : null;
}

/** `commands` is a JSON array string in responses — parse for display (guide §7.10). */
export function parseCheckCommands(raw: string): string[] {
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.map(String) : [];
  } catch {
    return raw ? [raw] : [];
  }
}

/**
 * Command check definition (guide §3.1): commands + match mode + server scope +
 * time window. Commands submit as a real array; the client validates end > start
 * (the backend 400s otherwise).
 */
export function CommandCheckModal({ isOpen, mode, check, presetEventId, onClose, onSuccess }: CommandCheckModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);
  useOnClickOutside(modalRef, onClose);

  const [title, setTitle] = useState('');
  const [commands, setCommands] = useState<string[]>([]);
  const [commandInput, setCommandInput] = useState('');
  const [matchMode, setMatchMode] = useState<EventCheckMatchMode>('prefix');
  const [scopeFqdn, setScopeFqdn] = useState('');
  const [scopeCluster, setScopeCluster] = useState('');
  const [timeStart, setTimeStart] = useState('');
  const [timeEnd, setTimeEnd] = useState('');
  const [status, setStatus] = useState<EventCheckStatus>('draft');
  const [notes, setNotes] = useState('');
  const [eventSearch, setEventSearch] = useState('');
  const debouncedEventSearch = useDebounce(eventSearch, 300);
  const [eventResults, setEventResults] = useState<EventItem[]>([]);
  const [selectedEvent, setSelectedEvent] = useState<EventItem | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setError(null);
    setCommandInput('');
    setEventSearch('');
    setEventResults([]);
    if (mode === 'edit' && check) {
      setTitle(check.title);
      setCommands(parseCheckCommands(check.commands));
      setMatchMode(check.match_mode);
      setScopeFqdn(check.scope_fqdn ?? '');
      setScopeCluster(check.scope_cluster ?? '');
      setTimeStart(msToLocalInput(check.time_start));
      setTimeEnd(msToLocalInput(check.time_end));
      setStatus(check.status);
      setNotes(check.notes ?? '');
      if (check.event_id) {
        eventTeamApiService
          .getEvent(check.event_id)
          .then((e) => setSelectedEvent(e))
          .catch(() => setSelectedEvent(null));
      } else {
        setSelectedEvent(null);
      }
    } else {
      setTitle('');
      setCommands([]);
      setMatchMode('prefix');
      setScopeFqdn('');
      setScopeCluster('');
      setTimeStart('');
      setTimeEnd('');
      setStatus('draft');
      setNotes('');
      setSelectedEvent(null);
      if (presetEventId) {
        eventTeamApiService
          .getEvent(presetEventId)
          .then((e) => {
            setSelectedEvent(e);
            // Prefill the window from the event's run window when present.
            if (e.scheduled_start) setTimeStart(msToLocalInput(e.scheduled_start));
            if (e.scheduled_end) setTimeEnd(msToLocalInput(e.scheduled_end));
          })
          .catch(() => setSelectedEvent(null));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || debouncedEventSearch.trim().length === 0) {
      setEventResults([]);
      return;
    }
    let cancelled = false;
    eventTeamApiService
      .searchEvents(debouncedEventSearch)
      .then((res) => {
        if (!cancelled) setEventResults(res);
      })
      .catch(() => {
        if (!cancelled) setEventResults([]);
      });
    return () => {
      cancelled = true;
    };
  }, [debouncedEventSearch, isOpen]);

  const addCommand = () => {
    const cmd = commandInput.trim();
    if (!cmd || commands.includes(cmd)) return;
    setCommands((prev) => [...prev, cmd]);
    setCommandInput('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const startMs = localInputToMs(timeStart);
    const endMs = localInputToMs(timeEnd);
    if (!title.trim() || commands.length === 0 || startMs == null || endMs == null) {
      setError('Title, at least one command, and a full time window are required.');
      return;
    }
    if (endMs <= startMs) {
      setError('The window end must be after the start (the backend rejects it otherwise).');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      if (mode === 'create') {
        const created = await eventTeamApiService.createCheck({
          title: title.trim(),
          commands,
          match_mode: matchMode,
          scope_fqdn: scopeFqdn.trim() || null,
          scope_cluster: scopeCluster.trim() || null,
          time_start: startMs,
          time_end: endMs,
          event_id: selectedEvent?.id ?? null,
          status,
          notes: notes.trim() || null,
        });
        onSuccess(created);
      } else if (check) {
        const updated = await eventTeamApiService.updateCheck(check.id, {
          title: title.trim(),
          commands,
          match_mode: matchMode,
          scope_fqdn: scopeFqdn.trim() || null,
          scope_cluster: scopeCluster.trim() || null,
          time_start: startMs,
          time_end: endMs,
          event_id: selectedEvent?.id ?? null,
          status,
          notes: notes.trim() || null,
        });
        onSuccess(updated);
      }
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save check');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const modalContent = (
    <div className="modal-overlay z-[9999]">
      <div
        ref={modalRef}
        className="modal-panel max-w-xl"
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 right-3 text-glass-400 hover:text-white p-2"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>

        <h2 className="text-lg font-semibold tracking-tight text-white mb-4 flex items-center gap-2">
          {mode === 'create' ? (
            <>
              <ShieldCheck className="w-6 h-6 text-accent-400" />
              New command check
            </>
          ) : (
            <>
              <Pencil className="w-6 h-6 text-accent-400" />
              Edit command check
            </>
          )}
        </h2>

        {error && (
          <div className="flex items-center gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/20 mb-4">
            <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0" />
            <span className="text-sm text-red-300">{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-sm text-glass-300 mb-1 block">Title *</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="glass-input w-full text-sm"
              placeholder="e.g. Heist Event teleport check"
              required
              autoFocus
            />
          </div>

          <div>
            <label className="text-sm text-glass-300 mb-1 block">Forbidden commands *</label>
            <div className="flex items-center gap-2 mb-2">
              <input
                type="text"
                value={commandInput}
                onChange={(e) => setCommandInput(e.target.value)}
                placeholder="/tp"
                className="glass-input text-sm flex-1 font-mono"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addCommand();
                  }
                }}
              />
              <button type="button" onClick={addCommand} disabled={!commandInput.trim()} className="glass-button-secondary !p-2">
                <Plus className="w-4 h-4" />
              </button>
            </div>
            {commands.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {commands.map((cmd) => (
                  <span
                    key={cmd}
                    className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-red-500/10 border border-red-500/20 text-xs text-red-300 font-mono"
                  >
                    {cmd}
                    <button
                      type="button"
                      onClick={() => setCommands((prev) => prev.filter((c) => c !== cmd))}
                      className="text-red-400/70 hover:text-red-300"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="text-sm text-glass-300 mb-1 block">Match mode</label>
              <select
                value={matchMode}
                onChange={(e) => setMatchMode(e.target.value as EventCheckMatchMode)}
                className="glass-input w-full text-sm capitalize"
              >
                {EVENT_CHECK_MATCH_MODES.map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-sm text-glass-300 mb-1 block">Lifecycle status</label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value as EventCheckStatus)}
                className="glass-input w-full text-sm capitalize"
              >
                {EVENT_CHECK_STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="text-sm text-glass-300 mb-1 block">Scope: server FQDN</label>
              <input
                type="text"
                value={scopeFqdn}
                onChange={(e) => setScopeFqdn(e.target.value)}
                className="glass-input w-full text-sm font-mono"
                placeholder="events.playcdu.co"
              />
            </div>
            <div>
              <label className="text-sm text-glass-300 mb-1 block">Scope: cluster id</label>
              <input
                type="text"
                value={scopeCluster}
                onChange={(e) => setScopeCluster(e.target.value)}
                className="glass-input w-full text-sm font-mono"
                placeholder="(optional)"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="text-sm text-glass-300 mb-1 block">Window start *</label>
              <input
                type="datetime-local"
                value={timeStart}
                onChange={(e) => setTimeStart(e.target.value)}
                className="glass-input w-full text-sm"
                required
              />
            </div>
            <div>
              <label className="text-sm text-glass-300 mb-1 block">Window end *</label>
              <input
                type="datetime-local"
                value={timeEnd}
                onChange={(e) => setTimeEnd(e.target.value)}
                className="glass-input w-full text-sm"
                required
              />
            </div>
          </div>

          <div>
            <label className="text-sm text-glass-300 mb-1 block">Linked event (optional)</label>
            {selectedEvent ? (
              <div className="flex items-center justify-between p-2 rounded-lg bg-accent-500/10 border border-accent-500/20 gap-2">
                <span className="text-sm font-medium text-white truncate">{selectedEvent.title}</span>
                <button
                  type="button"
                  onClick={() => setSelectedEvent(null)}
                  className="text-glass-400 hover:text-white flex-shrink-0 p-1"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-glass-400" />
                <input
                  type="text"
                  value={eventSearch}
                  onChange={(e) => setEventSearch(e.target.value)}
                  className="glass-input w-full pl-10 text-sm"
                  placeholder="Search events…"
                />
                {eventResults.length > 0 && eventSearch.length > 0 && (
                  <div className="absolute top-full left-0 right-0 mt-1 glass-card border border-glass-200/20 rounded-lg overflow-hidden z-10 max-h-48 overflow-y-auto">
                    {eventResults.map((e) => (
                      <button
                        key={e.id}
                        type="button"
                        onClick={() => {
                          setSelectedEvent(e);
                          setEventSearch('');
                          setEventResults([]);
                        }}
                        className="w-full text-left px-3 py-2 hover:bg-glass-200/10 text-sm text-white"
                      >
                        {e.title}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          <div>
            <label className="text-sm text-glass-300 mb-1 block">Notes</label>
            <input type="text" value={notes} onChange={(e) => setNotes(e.target.value)} className="glass-input w-full text-sm" />
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="glass-button-secondary text-sm">
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || !title.trim() || commands.length === 0 || !timeStart || !timeEnd}
              className="glass-button text-sm inline-flex items-center gap-1.5"
            >
              {submitting && <Loader className="w-4 h-4 animate-spin" />}
              {mode === 'create' ? 'Create check' : 'Save changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}
